import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import prefixProperties from '~/prix/utils/prefixProperties'
import attendanceStatsAction from './attendanceStats.action.json' with { type: 'json' }
import { z } from 'zod'
import { enumerated } from '~/prix/types/enumerated'
import expression from '~/prix/types/expression'
import { dateOrIsoString } from '~/prix/types/date'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { citizenLocationShape, citizenShapeBase } from '../../citizen/stats/citizenShape'
import { citizenHealthProfileShape } from '../../citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

export const attendanceShape = z.object({
  originalId: z.string().describe('ID original'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  professionalCns: z.string().describe('CNS do profissional'),
  professionalCbo: z.string().describe('CBO do profissional'),
  healthUnitCnes: z.string().describe('CNES da unidade de saúde'),
  teamIne: z.string().describe('INE da equipe de saúde'),
  professionalName: z.string().describe('Nome do profissional de saúde'),
  healthUnitName: z.string().describe('Nome da unidade de saúde'),
  teamName: z.string().describe('Nome da equipe de saúde').optional(),
  icd10: z.string().describe('Código CID-10'),
  icpc2: z.string().describe('Código CIAP-2'),
  icpc2Description: z.string().describe('CIAP-2'),
  icd10Description: z.string().describe('CID-10'),
  attendanceDateTime: dateOrIsoString().describe('Data e hora do atendimento'),
  typeOfAttendance: enumerated({
    consultations: 'Consultas',
    scheduledProgrammedConsultation: 'Consulta agendada programada',
    continuedCare: 'Cuidado continuado',
    scheduledConsultation: 'Consulta agendada',
    spontaneousDemand: 'Demanda espontânea',
    initialListeningOrOrientation: 'Escuta inicial ou Orientação',
    consultationOnTheDay: 'Consulta no dia',
    urgencyCare: 'Atendimento de urgência',
    programmedCare: 'Atendimento programado',
    nonProgrammedCare: 'Atendimento não programado',
    homeVisitAfterDeath: 'Visita domiciliar pós-óbito',
    notInformed: 'Não informado',
  }).describe('Tipo de atendimento'),
  weight: z.number().describe('Peso'),
  height: z.number().describe('Altura'),
  bmi: z.number().describe('IMC'),
  isVaccinationUpToDate: z.boolean().describe('Vacinação em dia'),
  lastMenstruationDate: z.string().describe('Data da última menstruação'),
  isObservation: z.boolean().describe('Observação'),
  isScheduleReturn: z.boolean().describe('Retorno para consulta agendada'),
  isScheduleForGroups: z.boolean().describe('Agendamento para grupos'),
  isScheduleForNasf: z.boolean().describe('Agendamento para NASF'),
  isScheduleForContinuedCare: z.boolean().describe('Retorno para cuidado continuado'),
  isEpisodeDischarge: z.boolean().describe('Alta do episódio avaliado'),
  hasReferralToSpecializedService: z
    .boolean()
    .describe('Encaminhamento para serviço especializado'),
  hasReferralToCaps: z.boolean().describe('Encaminhamento para CAPS'),
  hasReferralToHospitalization: z.boolean().describe('Encaminhamento para internação hospitalar'),
  hasReferralToUrgency: z.boolean().describe('Encaminhamento para urgência'),
  hasReferralToHomeCareService: z.boolean().describe('Encaminhamento para atenção domiciliar'),
  hasIntersectoralReferral: z.boolean().describe('Encaminhamento intersetorial'),
  displayName: z.string().describe('Nome'),
  ...prefixProperties('citizen.',citizenShapeBase.shape),
  ...prefixProperties('citizen.',citizenLocationShape.shape),
  ...prefixProperties('citizenHealth.', citizenHealthProfileShape.shape)
})
const attendanceExpression = expression(attendanceShape.shape)

export const attendanceVariable: Variable = {
  key: 'attendance',
  categoryKey: 'healthAttendances',
  actionDefinitionKey: attendanceStatsAction.key,
  title: 'Atendimentos',
  unitSingular: 'atendimento',
  unitPlural: 'atendimentos',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: getZodSchemaInfo(attendanceShape),
  filterableBy: getZodSchemaInfo(
    attendanceShape.pick({
      /* CIDADÃO */
      'citizen.age': true,
      'citizen.sex': true,
      'citizen.deceased': true,
      /* SAÚDE */
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.smoker': true,
      'citizenHealth.skinColorOrRace': true,
      'citizenHealth.educationLevel': true,
      'citizenHealth.employmentStatus': true,
      'citizenHealth.homeless': true,
      'citizenHealth.hasPrivateHealthInsurance': true,
      'citizenHealth.nationality': true,
      /* ATENDIMENTO */
      typeOfAttendance: true,
      isVaccinationUpToDate: true,
      bmi: true,
    }),
  ),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.attendance,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    /* ATENDIMENTO */
    'attendanceDateTime',
    'professionalName',
    'healthUnitName',
    'icd10Description',
    'icpc2Description',
    'teamName',
    'weight',
    'height',
    'typeOfAttendance',  
    'isVaccinationUpToDate',
    'lastMenstruationDate',
    'isObservation',
    'isScheduleReturn',
    'isScheduleForGroups',
    'isScheduleForNasf',
    'isScheduleForContinuedCare',
    'isEpisodeDischarge',
    'hasReferralToSpecializedService',
    'hasReferralToCaps',
    'hasReferralToHospitalization',
    'hasReferralToUrgency',
    'hasIntersectoralReferral',
    'hasReferralToHomeCareService',
    /* CIDADÃO */
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.originalId',

    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
    
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'trend',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 0,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(attendanceExpression),
}

const attendanceStatsDefinition = defineAction({
  ...attendanceStatsAction,
  input: z.object({
    variable,
    highlight: attendanceExpression.optional(),
    filter: attendanceExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(attendanceShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default attendanceStatsDefinition
